import React, { useState, useMemo, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Stack,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    CircularProgress,
    IconButton,
    Card,
    CardContent,
    Avatar,
    Collapse
} from '@mui/material';
import { DateTime } from 'luxon';
import {
    Close as CloseIcon,
    Person as PersonIcon,
    History as HistoryIcon,
    Pause as PauseIcon,
    PlayArrow as ResumeIcon,
    Cancel as CancelIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    CalendarToday as CalendarIcon,
    InfoOutlined as InfoIcon
} from '@mui/icons-material';
import { getStatusStyles, mapApplicationStatus } from '../utils/util';
import ApplicationStatusComponent from './ApplicationStatusComponent';
import PauseApplicationDialog from './PauseApplicationDialog';
import CancelApplicationDialog from './CancelApplicationDialog';
import ResumeApplicationDialog from './ResumeApplicationDialog';
import { employerApi } from '../services/apiClient';
import { toast } from 'react-toastify';

interface ApplicationDetailsDialogProps {
    open: boolean;
    onClose: () => void;
    application: any | null;
    onApplicationUpdated?: () => void;
}

const ApplicationDetailsDialog = ({ open, onClose, application, onApplicationUpdated }: ApplicationDetailsDialogProps) => {
    const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [detailedApplication, setDetailedApplication] = useState<any | null>(null);
    const [productOptions, setProductOptions] = useState<{ upgrades: any[]; downgrades: any[] }>({ upgrades: [], downgrades: [] });
    const [productOptionsLoading, setProductOptionsLoading] = useState(false);
    const [productOptionsError, setProductOptionsError] = useState<string | null>(null);
    const [showProductOptions, setShowProductOptions] = useState(false);
    const [selectedProductToChangeTo, setSelectedProductToChangeTo] = useState<any | null>(null);
    const [changeProductLoading, setChangeProductLoading] = useState(false);

    const currentApplication = detailedApplication || application;

    const productInfoCard = (product: any, clickable = false) => (
        <div
            key={product.id}
            onClick={() => clickable && handleSelectProductOption(product)}
            className={`bg-white flex flex-row w-full relative p-2 border-b-2 items-center transform duration-300 ${clickable ? 'hover:bg-[#86eb85]/50 hover:rounded-lg hover:border-b-[#86eb85] cursor-pointer' : ''
                }`}
        >
            {product?.imageURL && (
                <div className="h-10 w-10 min-w-[60px] min-h-[60px] md:h-12 md:w-12 m-2 rounded-3xl overflow-hidden flex-none">
                    <img src={product.imageURL} className="w-full h-full object-cover" alt={product?.name} />
                </div>
            )}
            <div className="flex flex-col items-start max-w-1/2">
                <p className="text-lg text-primary text-left font-montserrat font-medium">{product?.name}</p>
            </div>
        </div>
    );

    const renderActionButton = (text: string, onClick: () => void, type: string = '') => (
        <div
            onClick={onClick}
            // sx={{ border: '1px solid #000' }}
            className={`flex flex-row w-full items-center justify-center px-4 py-2 rounded-2xl h-full transition-all font-montserrat
                duration-200 cursor-pointer bg-white border border-black text-black  ${type == 'success' ? ' border-2 border-green-500 hover:bg-green-50 ' : type == "cancel" ? 'border-2 border-red hover:bg-red/10' : 'border-black'}`}
        >
            <p className={`text-base font-medium my-0 text-black font-montserrat text-center ${type == 'success' ? 'text-green-600' : type == "cancel" ? 'text-red' : ''}`}>{text}</p>
        </div>
    );

    const fetchApplicationDetails = async (applicationId: string) => {
        try {
            setProductOptionsLoading(true);
            setProductOptionsError(null);
            const response: any = await employerApi.get(`/applications/${applicationId}`);
            const appData = response?.data || response;
            if (appData) {
                setDetailedApplication(appData);
                setProductOptions({
                    upgrades: appData.upgradeOptions || [],
                    downgrades: appData.downgradeOptions || []
                });
            }
        } catch (error: any) {
            console.error('Failed to fetch detailed application', error);
            setProductOptionsError(error?.message || 'Unable to load product options');
        } finally {
            setProductOptionsLoading(false);
        }
    };

    useEffect(() => {
        if (open && application?.id) {
            setDetailedApplication(application);
            setProductOptions({ upgrades: [], downgrades: [] });
            setShowProductOptions(false);
            setSelectedProductToChangeTo(null);
            setChangeProductLoading(false);
            fetchApplicationDetails(application.id);
        }

        if (!open) {
            setShowProductOptions(false);
            setSelectedProductToChangeTo(null);
            setChangeProductLoading(false);
        }
    }, [open, application?.id]);

    const availableProductOptions = useMemo(() => {
        return [
            ...(productOptions.upgrades || []),
            ...(productOptions.downgrades || [])
        ];
    }, [productOptions]);
    const productChangeOptions = availableProductOptions;

    const canChangeProduct = useMemo(() => {
        if (!currentApplication) return false;
        if (currentApplication.status !== 'ACTIVE') return false;
        if (currentApplication.status === 'PENDING_PRODUCT_CHANGE') return false;
        return availableProductOptions.length > 0;
    }, [currentApplication, availableProductOptions]);

    const canPause = useMemo(() => {
        return currentApplication?.status === 'ACTIVE' && currentApplication?.benefit?.isPausable;
    }, [currentApplication]);

    const canResume = useMemo(() => {
        if (currentApplication?.status !== 'PAUSED') return false;
        const pauseEndDate = currentApplication?.pauseConfiguration?.pauseEndDate;
        if (!pauseEndDate) return false;
        return DateTime.fromISO(pauseEndDate) > DateTime.now();
    }, [currentApplication]);

    const canCancel = useMemo(() => {
        return currentApplication?.status !== 'COMPLETED' && currentApplication?.status !== 'CANCELLED';
    }, [currentApplication]);

    const activityLog = useMemo(() => {
        if (!currentApplication?.activityLog) return [];
        return [...currentApplication.activityLog].reverse();
    }, [currentApplication]);

    const handlePauseSuccess = () => {
        setPauseDialogOpen(false);
        toast.success('Application paused successfully');
        onApplicationUpdated?.();
    };

    const handleCancelSuccess = () => {
        setCancelDialogOpen(false);
        toast.success('Application cancelled successfully');
        onApplicationUpdated?.();
        onClose();
    };

    const handleResumeSuccess = () => {
        setResumeDialogOpen(false);
        toast.success('Application resumed successfully');
        onApplicationUpdated?.();
    };

    const handleSelectProductOption = (option: any) => {
        setSelectedProductToChangeTo(option);
    };

    const handleConfirmProductChange = async () => {
        if (!currentApplication || !selectedProductToChangeTo) return;
        try {
            setChangeProductLoading(true);
            await employerApi.post(`/applications/${currentApplication.id}/change-product`, {
                toBenefitID: selectedProductToChangeTo.id
            });
            toast.success('Product change requested successfully');
            setSelectedProductToChangeTo(null);
            setShowProductOptions(false);
            onApplicationUpdated?.();
            fetchApplicationDetails(currentApplication.id);
        } catch (error: any) {
            console.error('Failed to change product', error);
            const message = error?.response?.data?.message || error?.message || 'Failed to change product';
            toast.error(message);
        } finally {
            setChangeProductLoading(false);
        }
    };

    if (!currentApplication) return null;

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        maxHeight: '90vh',
                        borderRadius: 1
                    }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                }}>
                    <Box>
                        <h3>
                            Application Details - {currentApplication.id}
                        </h3>


                    </Box>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{
                            color: 'text.secondary'
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ overflowY: 'auto', p: 3 }}>

                    {/* Status & Created */}
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 py-2">
                        <div className="flex flex-col">
                            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Current Status</p>
                            <ApplicationStatusComponent application={currentApplication} />
                        </div>
                        <div className="text-left md:text-right">
                            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1 pt-2">Created</p>
                            <p className="text-sm font-semibold">
                                {DateTime.fromISO(currentApplication.creationDateTime).toFormat('dd LLL yyyy')}
                            </p>
                            <p className="text-xs text-gray-500">
                                {DateTime.fromISO(currentApplication.creationDateTime).toFormat('HH:mm')}
                            </p>
                        </div>
                    </div>
                    {currentApplication.docusealSubmission?.signedContractLink && (
                        <div className="mb-4">
                            <a
                                href={currentApplication.docusealSubmission.signedContractLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-300 bg-white text-sm font-semibold text-[#111] hover:bg-gray-50 transition-colors"
                            >
                                <img src={require("../assets/download.png")} className="w-4 h-4" />
                                <p className="text-sm font-semibold text-[#111]">Download signed contract</p>
                            </a>
                        </div>
                    )}
                    <div className="border-b border-gray-200 mb-3" />
                    <Stack spacing={3}>
                        {/* Product Overview */}
                        <div>
                            <div className="flex flex-row items-center justify-center flex-wrap gap-1 my-2 border p-2 rounded-3xl bg-[#666]">
                                <div className="flex flex-col w-full relative p-2 rounded-2xl items-start bg-white">
                                    <div className="flex flex-row w-full items-center justify-between">

                                        {currentApplication.benefit && (
                                            <div className="flex flex-row items-center justify-center ">
                                                <div className="h-10 w-10 min-w-[50px] min-h-[50px] md:h-12 md:w-12 m-2 rounded-full overflow-hidden flex-none">
                                                    <img src={currentApplication.benefit?.imageURL} className="w-full h-full object-cover" alt={currentApplication.benefit?.name} />
                                                </div>

                                                <p className="text-lg md:text-xl text-primary text-left font-montserrat font-[1000]">
                                                    {currentApplication.benefit.name}
                                                </p>

                                            </div>
                                        )}
                                        {currentApplication.benefit?.renewalConfiguration?.distributionMethod === 'CYCLEPASS' ? (
                                            <div className="flex flex-col items-center justify-end">
                                                <p className="text-gray-600 text-sm my-0    ">Monthly Top-up Amount</p>
                                                <p className="text-xl font-bold text-primary my-0">£{currentApplication.cyclePassConfig?.amount?.toFixed(2)}</p>
                                            </div>
                                        ) : currentApplication.benefit?.renewalConfiguration?.distributionMethod === 'MANUAL' ? (
                                            <div className="flex flex-col items-center justify-end">
                                                <p className="text-gray-600 text-sm my-0">Requested Amount</p>
                                                <p className="text-xl font-bold text-primary my-0">£{Number(currentApplication.requestedAmount?.amountPounds || 0).toFixed(2)}</p>
                                                <p className="text-gray-600 text-xs my-0">
                                                    £{(Number(currentApplication.requestedAmount?.amountPounds || 0) / (currentApplication.salarySacrificeTerms?.numberOfSacrifices > 0 ? currentApplication.salarySacrificeTerms.numberOfSacrifices : 12)).toFixed(2)} per month
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="flex flex-row items-center justify-center p-3">
                                                {currentApplication.benefit?.renewalConfiguration?.recurrence === 'ANNUAL' ? (
                                                    <div className="flex flex-col items-end justify-end">
                                                        <p className="text-gray-600 text-sm my-0">Annual Cost</p>
                                                        <p className="text-xl font-bold text-primary my-0">{currentApplication.benefit?.originalAnnualCost}</p>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col items-end justify-end">
                                                        <p className="text-gray-600 text-sm my-0">Monthly Cost</p>
                                                        <p className="text-xl font-bold text-primary my-0">{currentApplication.benefit?.originalCost}</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {currentApplication.status === 'PENDING_PRODUCT_CHANGE' && (
                                        <div className="ml-2 flex flex-row items-center my-2">
                                            <InfoIcon sx={{ fontSize: 18, color: '#f97316', mr: 1 }} />
                                            <p className="text-red font-bold ml-1 text-sm italic ">
                                                There is a change pending on this product.
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {currentApplication.status === 'PENDING_PRODUCT_CHANGE' && currentApplication.pendingBenefit && (
                                    <div className="flex flex-col w-full relative p-2 rounded-2xl items-start bg-green-100 mt-1">

                                        <div className="flex flex-row items-center">

                                            <div className="h-10 w-10 min-w-[50px] min-h-[50px] md:h-12 md:w-12 m-2 rounded-full overflow-hidden flex-none">
                                                <img src={currentApplication.pendingBenefit?.imageURL} className="w-full h-full object-cover" alt={currentApplication.pendingBenefit?.name} />
                                            </div>

                                            <p className="text-lg md:text-xl text-primary text-left font-montserrat font-[1000]">
                                                {currentApplication.pendingBenefit.name}
                                            </p>

                                        </div>
                                        <div className="ml-2 flex flex-row items-center my-2">
                                            <InfoIcon sx={{ fontSize: 18, color: '#0f172a', mr: 1 }} />
                                            <p className="text-darkgrey font-bold ml-2 text-sm italic">
                                                This product will activate on {DateTime.fromISO(currentApplication.pendingBenefit.effectiveDate).toFormat('dd LLL yyyy')}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {showProductOptions && (
                                    <div className="font-light w-full px-4 pt-2 pb-4 bg-white mt-1 rounded-3xl">
                                        <h3
                                            className="text-left cursor-pointer"
                                            onClick={() => setShowProductOptions(!showProductOptions)}
                                        >
                                            The product is eligible for change to one of the below:
                                        </h3>

                                        {productOptionsLoading ? (
                                            <div className="flex flex-row items-center gap-2 mt-2">
                                                <CircularProgress size={20} />
                                                <p className="text-sm text-gray-500">Loading available products...</p>
                                            </div>
                                        ) : productChangeOptions.length > 0 ? (
                                            <div className="flex flex-row flex-wrap gap-1 w-full mt-2">
                                                {productChangeOptions.map((product: any) => productInfoCard(product, true))}
                                            </div>
                                        ) : (
                                            <p className="text-left text-sm mt-2 text-gray-500">
                                                No alternative products are available for this benefit.
                                            </p>
                                        )}
                                        {productOptionsError && (
                                            <p className="text-red text-sm mt-2">{productOptionsError}</p>
                                        )}
                                    </div>
                                )}

                                {!showProductOptions && !currentApplication.pendingBenefit && (
                                    productChangeOptions.length > 0 ? (
                                        <div className="flex flex-row items-center justify-center gap-1 w-full mt-1">
                                            {renderActionButton('Change Product', () => setShowProductOptions(true))}
                                        </div>
                                    ) : (
                                        <div className="flex flex-row items-center justify-center gap-1 w-full mt-1 bg-white p-1 rounded-3xl">
                                            <p className="text-left text-sm">This product is not eligible for change.</p>
                                        </div>
                                    )
                                )}

                                {showProductOptions && (
                                    <div className="flex flex-row items-center justify-center gap-1 w-full mt-1">
                                        {renderActionButton('Cancel product change', () => setShowProductOptions(false), 'error')}
                                    </div>
                                )}

                                {productOptionsError && !showProductOptions && (
                                    <div className="flex flex-row items-center justify-center gap-1 w-full mt-1 bg-white p-1 rounded-3xl">
                                        <p className="text-red text-sm text-center w-full">{productOptionsError}</p>
                                    </div>
                                )}
                            </div>
                        </div>


                        {/* Employee Information */}
                        <Box>
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                                <PersonIcon sx={{ color: '#666', fontSize: 20 }} />

                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                                    Employee
                                </Typography>
                            </Stack>
                            <Stack spacing={1}>
                                <Box>

                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.7rem' }}>
                                        Name
                                    </Typography>
                                    <Typography variant="body2" sx={{ ml: 2.5 }}>
                                        {currentApplication.employee?.firstName} {currentApplication.employee?.surname}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                        Email
                                    </Typography>
                                    <Typography variant="body2" sx={{ ml: 2.5 }}>
                                        {currentApplication.employee?.email}
                                    </Typography>
                                </Box>
                                {currentApplication.employee?.phone && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                            Phone
                                        </Typography>
                                        <Typography variant="body2" sx={{ ml: 2.5 }}>
                                            {currentApplication.employee.phone}
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>
                        </Box>

                        {/* Additional Details */}
                        {(currentApplication.pauseConfiguration || currentApplication.cancellationDateTime || currentApplication.salarySacrificeTerms) && (
                            <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                                    Additional Information
                                </Typography>
                                <Stack spacing={2}>
                                    {currentApplication.pauseConfiguration && (
                                        <Box>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                                <PauseIcon sx={{ color: '#666', fontSize: 18 }} />
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    Pause Information
                                                </Typography>
                                            </Stack>
                                            <Stack spacing={0.5} sx={{ ml: 3.5 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    <strong>Duration:</strong> {currentApplication.pauseConfiguration.pauseDuration} month{currentApplication.pauseConfiguration.pauseDuration > 1 ? 's' : ''}
                                                </Typography>
                                                {currentApplication.pauseConfiguration.pauseStartDate && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        <strong>Started:</strong> {DateTime.fromISO(currentApplication.pauseConfiguration.pauseStartDate).toFormat('dd LLL yyyy')}
                                                    </Typography>
                                                )}
                                                {currentApplication.pauseConfiguration.pauseEndDate && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        <strong>Resumes:</strong> {DateTime.fromISO(currentApplication.pauseConfiguration.pauseEndDate).toFormat('dd LLL yyyy')}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </Box>
                                    )}
                                    {currentApplication.cancellationDateTime && (
                                        <Box>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                                <CancelIcon sx={{ color: '#666', fontSize: 18 }} />
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    Cancellation
                                                </Typography>
                                            </Stack>
                                            <Stack spacing={0.5} sx={{ ml: 3.5 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    <strong>Cancelled:</strong> {DateTime.fromISO(currentApplication.cancellationDateTime).toFormat('dd LLL yyyy HH:mm')}
                                                </Typography>
                                                {currentApplication.cancellationReason && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        <strong>Reason:</strong> {currentApplication.cancellationReason}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </Box>
                                    )}
                                    {currentApplication.salarySacrificeTerms && (
                                        <Box>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                                <CalendarIcon sx={{ color: '#666', fontSize: 18 }} />
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    Salary Sacrifice Terms
                                                </Typography>
                                            </Stack>
                                            <Typography variant="body2" color="text.secondary" sx={{ ml: 3.5 }}>
                                                {currentApplication.salarySacrificeTerms.numberOfSacrifices > 0
                                                    ? `This product is being sacrificed over ${currentApplication.salarySacrificeTerms.numberOfSacrifices} months`
                                                    : 'The sacrifices for this product occur monthly until cancelled.'}
                                            </Typography>
                                        </Box>
                                    )}
                                </Stack>
                            </Box>
                        )}

                        {/* Activity History */}
                        <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                                <HistoryIcon sx={{ color: '#666', fontSize: 20 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                                    Activity History
                                </Typography>
                            </Stack>
                            {activityLog.length > 0 ? (
                                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600, py: 1.5, fontSize: '0.75rem', color: 'text.secondary' }}>Date & Time</TableCell>
                                                <TableCell sx={{ fontWeight: 600, py: 1.5, fontSize: '0.75rem', color: 'text.secondary' }}>Action</TableCell>
                                                <TableCell sx={{ fontWeight: 600, py: 1.5, fontSize: '0.75rem', color: 'text.secondary' }}>Details</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {activityLog.map((item: any, index: number) => (
                                                <TableRow
                                                    key={index}
                                                    sx={{
                                                        '&:hover': { bgcolor: '#fafafa' }
                                                    }}
                                                >
                                                    <TableCell sx={{ py: 1.5 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {DateTime.fromISO(item.timestamp).toFormat('dd LLL yyyy')}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {DateTime.fromISO(item.timestamp).toFormat('HH:mm')}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ py: 1.5 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {item.type}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ py: 1.5 }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {item.type == "Voucher Assigned" ? 'A new voucher was assigned for this month and shared with the employee.' : item.action}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 3 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No activity history available.
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Action Buttons */}

                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    {(canPause || canResume || canCancel) && (

                        <Stack direction="row" spacing={2} flexWrap="wrap">
                            {canPause && (
                                <Button
                                    variant="outlined"
                                    startIcon={<PauseIcon sx={{ color: '#666' }} />}
                                    onClick={() => setPauseDialogOpen(true)}
                                    disabled={isLoading}
                                    sx={{
                                        borderColor: '#ddd',
                                        color: '#222',
                                        '&:hover': {
                                            borderColor: '#999',
                                            bgcolor: '#f5f5f5'
                                        }
                                    }}
                                >
                                    Pause Application
                                </Button>
                            )}
                            {canResume && (
                                <Button
                                    variant="outlined"
                                    startIcon={<ResumeIcon sx={{ color: '#666' }} />}
                                    onClick={() => setResumeDialogOpen(true)}
                                    disabled={isLoading}
                                    sx={{
                                        borderColor: '#ddd',
                                        color: '#222',
                                        '&:hover': {
                                            borderColor: '#999',
                                            bgcolor: '#f5f5f5'
                                        }
                                    }}
                                >
                                    Resume Application
                                </Button>
                            )}
                            {canCancel && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<CancelIcon />}
                                    onClick={() => setCancelDialogOpen(true)}
                                    disabled={isLoading}
                                    sx={{
                                        borderColor: '#ddd',
                                        '&:hover': {
                                            borderColor: '#999',
                                            bgcolor: '#f5f5f5'
                                        }
                                    }}
                                >
                                    Cancel Application
                                </Button>
                            )}
                        </Stack>
                    )}

                </DialogActions>
            </Dialog>

            {/* Pause Dialog */}
            <PauseApplicationDialog
                open={pauseDialogOpen}
                onClose={() => setPauseDialogOpen(false)}
                application={currentApplication}
                onSuccess={handlePauseSuccess}
            />

            {/* Cancel Dialog */}
            <CancelApplicationDialog
                open={cancelDialogOpen}
                onClose={() => setCancelDialogOpen(false)}
                application={currentApplication}
                onSuccess={handleCancelSuccess}
            />

            {/* Resume Dialog */}
            <ResumeApplicationDialog
                open={resumeDialogOpen}
                onClose={() => setResumeDialogOpen(false)}
                application={currentApplication}
                onSuccess={handleResumeSuccess}
            />

            {selectedProductToChangeTo && (
                <div
                    className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
                    style={{ zIndex: 2000 }}
                >
                    {changeProductLoading ? (
                        <div className="flex flex-col bg-white p-8 rounded-lg items-center justify-center shadow-lg m-4 gap-2">
                            <CircularProgress size={50} style={{ color: '#86eb85' }} />
                            <p className="text-center">We&apos;re updating the selected product...</p>
                        </div>
                    ) : (
                        <div className="flex flex-col bg-white p-8 rounded-lg shadow-lg m-4 gap-4 max-w-lg w-full">
                            <p className="text-base">You have selected to change this subscription's product to a <strong>{selectedProductToChangeTo.name}.</strong></p>
                            <Typography variant="body2" sx={{ textAlign: 'left', fontWeight: 500 }}>
                                The employee will receive a confirmation email outlining the change from {currentApplication.benefit?.name} to {selectedProductToChangeTo.name}.
                            </Typography>
                            {renderActionButton(
                                `Change product to ${selectedProductToChangeTo.name}`,
                                handleConfirmProductChange,
                                'success'

                            )}
                            {renderActionButton('Go Back', () => setSelectedProductToChangeTo(null), 'cancel')}
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default ApplicationDetailsDialog;

